import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaWordpress, FaSync, FaTrash, FaExclamationTriangle, FaArrowLeft, FaBox, FaReceipt } from 'react-icons/fa';
import httpRequest from '../utils/request';

const WooCommerce = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [connecting, setConnecting] = useState(false);
    const [config, setConfig] = useState({
        storeUrl: '',
        consumerKey: '',
        consumerSecret: ''
    });
    const [isConnected, setIsConnected] = useState(false);
    const [orders, setOrders] = useState([]);
    const [activeTab, setActiveTab] = useState('orders'); // 'orders', 'sync', 'settings'

    const loadData = React.useCallback(async () => {
        try {
            setLoading(true);
            const res = await httpRequest.get('/ecommerce/config');
            const ecommerce = res.data;

            if (ecommerce.credentials?.woocommerce?.consumerKey) {
                setConfig({
                    storeUrl: ecommerce.credentials.woocommerce.storeUrl || '',
                    consumerKey: ecommerce.credentials.woocommerce.consumerKey || '',
                    consumerSecret: '************' // No mostrar secreto real
                });
                setIsConnected(true);
                loadOrders();
            }
        } catch (error) {
            console.error('Error cargando configuración:', error);
            toast.error('Error al cargar la configuración');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const loadOrders = async () => {
        try {
            const res = await httpRequest.get('/woocommerce/orders');
            setOrders(res.data);
        } catch (error) {
            console.error('Error cargando pedidos:', error);
        }
    };

    const handleConnect = async (e) => {
        e.preventDefault();
        try {
            setConnecting(true);
            await httpRequest.post('/woocommerce/connect', config);
            toast.success('¡Conexión con WooCommerce exitosa!');
            setIsConnected(true);
            loadOrders();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error al conectar');
        } finally {
            setConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('¿Estás seguro de desconectar WooCommerce?')) return;
        try {
            await httpRequest.delete('/woocommerce/disconnect');
            toast.success('Desconectado');
            setIsConnected(false);
            setConfig({ storeUrl: '', consumerKey: '', consumerSecret: '' });
            setOrders([]);
        } catch (error) {
            toast.error('Error al desconectar');
        }
    };

    if (loading) return <div style={{ padding: '50px', textAlign: 'center' }}>Cargando módulo...</div>;

    return (
        <div style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto', fontFamily: "'Inter', sans-serif" }}>

            {/* Header Animado */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
                <div>
                    <h1 style={{ fontSize: '32px', fontWeight: '800', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <FaWordpress style={{ color: '#21759b' }} /> WooCommerce <span style={{ fontSize: '14px', background: isConnected ? '#e8f5e9' : '#ffebee', color: isConnected ? '#2e7d32' : '#c62828', padding: '4px 12px', borderRadius: '20px', fontWeight: 'bold' }}>{isConnected ? 'CONECTADO' : 'DESCONECTADO'}</span>
                    </h1>
                    <p style={{ color: '#666', marginTop: '5px' }}>Gestiona tu tienda online y sincroniza tu inventario en tiempo real.</p>
                </div>
                <button onClick={() => navigate('/dashboard')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '12px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', transition: 'all 0.3s' }}>
                    <FaArrowLeft /> Volver
                </button>
            </div>

            {!isConnected ? (
                /* Card de Conexión Premium */
                <div style={{ background: '#fff', padding: '40px', borderRadius: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', maxWidth: '600px', margin: '0 auto' }}>
                    <h2 style={{ marginBottom: '25px', fontSize: '22px', fontWeight: '700' }}>Configurar Conexión</h2>
                    <form onSubmit={handleConnect} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', color: '#555', fontWeight: '500' }}>URL de la Tienda (ej: https://mitienda.com)</label>
                            <input
                                type="url"
                                required
                                value={config.storeUrl}
                                onChange={(e) => setConfig({ ...config, storeUrl: e.target.value })}
                                placeholder="https://"
                                style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid #eee', outline: 'none', background: '#f8f9fa' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', color: '#555', fontWeight: '500' }}>Consumer Key (CK_...)</label>
                            <input
                                type="text"
                                required
                                value={config.consumerKey}
                                onChange={(e) => setConfig({ ...config, consumerKey: e.target.value })}
                                style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid #eee', outline: 'none', background: '#f8f9fa' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', color: '#555', fontWeight: '500' }}>Consumer Secret (CS_...)</label>
                            <input
                                type="password"
                                required
                                value={config.consumerSecret}
                                onChange={(e) => setConfig({ ...config, consumerSecret: e.target.value })}
                                style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid #eee', outline: 'none', background: '#f8f9fa' }}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={connecting}
                            style={{ padding: '15px', borderRadius: '12px', background: '#21759b', color: '#fff', border: 'none', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer', transition: '0.3s', marginTop: '10px' }}>
                            {connecting ? 'Validando conexión...' : 'Conectar Tienda'}
                        </button>
                    </form>
                    <div style={{ marginTop: '25px', padding: '15px', borderRadius: '12px', background: '#fff8e1', border: '1px solid #ffe082', fontSize: '14px', color: '#856404', display: 'flex', gap: '10px' }}>
                        <FaExclamationTriangle style={{ flexShrink: 0, marginTop: '3px' }} />
                        <p>Asegúrate de que la API REST esté habilitada en WooCommerce y que las claves tengan permisos de **Lectura/Escritura**.</p>
                    </div>
                </div>
            ) : (
                /* Dashboard de Control */
                <>
                    {/* Tabs Estilo Glassmorphism */}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '30px' }}>
                        {['orders', 'sync', 'settings'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                style={{
                                    padding: '12px 25px',
                                    borderRadius: '14px',
                                    border: 'none',
                                    background: activeTab === tab ? '#21759b' : '#fff',
                                    color: activeTab === tab ? '#fff' : '#666',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                    transition: 'all 0.3s'
                                }}
                            >
                                {tab === 'orders' ? '📦 Pedidos Recientes' : tab === 'sync' ? '🔄 Sincronización' : '⚙️ Configuración'}
                            </button>
                        ))}
                    </div>

                    <div style={{ padding: '30px', background: '#fff', borderRadius: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                        {activeTab === 'orders' && (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                    <h3 style={{ margin: 0 }}>Últimos Pedidos</h3>
                                    <button onClick={loadOrders} style={{ background: 'none', border: 'none', color: '#21759b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: '600' }}><FaSync /> Actualizar</button>
                                </div>

                                {orders.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>No hay pedidos recientes.</div>
                                ) : (
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ textAlign: 'left', borderBottom: '2px solid #f8f9fa', color: '#adb5bd' }}>
                                                    <th style={{ padding: '15px' }}># Pedido</th>
                                                    <th style={{ padding: '15px' }}>Fecha</th>
                                                    <th style={{ padding: '15px' }}>Cliente</th>
                                                    <th style={{ padding: '15px' }}>Total</th>
                                                    <th style={{ padding: '15px' }}>Estado</th>
                                                    <th style={{ padding: '15px' }}>Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orders.map(order => (
                                                    <tr key={order.id} style={{ borderBottom: '1px solid #f8f9fa', transition: '0.2s' }}>
                                                        <td style={{ padding: '15px', fontWeight: 'bold' }}>#{order.id}</td>
                                                        <td style={{ padding: '15px' }}>{new Date(order.date_created).toLocaleDateString()}</td>
                                                        <td style={{ padding: '15px' }}>{order.billing?.first_name} {order.billing?.last_name}</td>
                                                        <td style={{ padding: '15px', fontWeight: 'bold' }}>${parseFloat(order.total).toLocaleString()}</td>
                                                        <td style={{ padding: '15px' }}>
                                                            <span style={{ padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', background: '#e3f2fd', color: '#1976d2' }}>{order.status.toUpperCase()}</span>
                                                        </td>
                                                        <td style={{ padding: '15px' }}>
                                                            <button style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #eee', background: '#fff', cursor: 'pointer' }} title="Ver detalles"><FaReceipt /></button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}

                        {activeTab === 'sync' && (
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                                <FaBox size={50} color="#ddd" style={{ marginBottom: '20px' }} />
                                <h3>Gestión de Catálogo</h3>
                                <p style={{ color: '#666', maxWidth: '400px', margin: '0 auto 30px' }}>Asocia tus productos del POS con los de WooCommerce para mantener el stock y precios sincronizados.</p>
                                <button style={{ padding: '12px 24px', borderRadius: '12px', background: '#21759b', color: '#fff', border: 'none', fontWeight: '600', cursor: 'pointer' }}>Explorar Productos de WC</button>
                            </div>
                        )}

                        {activeTab === 'settings' && (
                            <div>
                                <h3 style={{ marginBottom: '20px' }}>Opciones del Módulo</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px', borderRadius: '16px', background: '#f8f9fa' }}>
                                        <div>
                                            <h4 style={{ margin: 0 }}>Desconectar Tienda</h4>
                                            <p style={{ margin: '5px 0 0', color: '#666', fontSize: '14px' }}>Remover credenciales y detener sincronización.</p>
                                        </div>
                                        <button onClick={handleDisconnect} style={{ color: '#c62828', background: 'none', border: 'none', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}><FaTrash /> Desconectar</button>
                                    </div>
                                    <div style={{ padding: '20px', borderRadius: '16px', background: '#f8f9fa' }}>
                                        <h4 style={{ margin: 0 }}>URL de Webhook</h4>
                                        <p style={{ margin: '5px 0 10px', color: '#666', fontSize: '14px' }}>Copia esta URL en WooCommerce {'>'} Configuración {'>'} Avanzado {'>'} Webhooks para recibir pedidos automáticamente.</p>
                                        <div style={{ padding: '10px', background: '#fff', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', fontFamily: 'monospace' }}>
                                            {window.location.origin}/api/woocommerce/webhook
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
                tr:hover { background-color: #fdfdfd; }
            `}</style>
        </div>
    );
};

export default WooCommerce;
